import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { usersApi, employeesApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  MessageSquare,
  FileText,
  Eye,
  Timer,
  Settings,
  Send,
  Trash2,
  User,
  ExternalLink,
  Link as LinkIcon,
  Calendar,
  Upload,
  Hash,
  AlignLeft,
  Mail,
  ListChecks,
  Loader2,
  DollarSign,
  ArrowDownLeft,
  ArrowUpRight,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import CreateClientRequestDialog from "../components/CreateClientRequestDialog";

const ClientRequestsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [priceRequests, setPriceRequests] = useState([]); // Incoming from partners
  const [studioRequests, setStudioRequests] = useState([]); // Outgoing to partners (price agreements)
  const [editingReq, setEditingReq] = useState(null); // For inline price editing
  const [editForm, setEditForm] = useState({ price_per_unit: "", quantity: "", discount: "", amount: "" });
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("outgoing"); // "outgoing" | "incoming"
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [managerFilter, setManagerFilter] = useState("all");
  const [priceStatusFilter, setPriceStatusFilter] = useState("pending"); // "pending" | "approved" | "rejected"
  const [settingsRequest, setSettingsRequest] = useState(null); // Request being edited
  const [requestSettings, setRequestSettings] = useState({
    response_time_hours: 48,
    penalty_rate_per_day: 0,
  });
  const [viewRequest, setViewRequest] = useState(null); // Request being viewed
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // Refresh every minute for timer updates
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, priceReqRes, studioReqRes, clientsRes, employeesRes] = await Promise.all([
        api.get("/client-requests"),
        api.get("/price-requests?status=all").catch(() => ({ data: [] })),
        api.get("/studio-requests").catch(() => ({ data: [] })),
        usersApi.getAll("client"),
        employeesApi.getAll(),
      ]);
      setRequests(requestsRes.data);
      setPriceRequests(priceReqRes.data || []);
      setStudioRequests(studioReqRes.data || []);
      setClients(clientsRes.data);
      const employees = employeesRes.data || [];
      const pms = employees.filter(e => 
        (e.position === "project_manager" || e.position === "admin") && e.is_active
      );
      setManagers(pms);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const openSettings = (request) => {
    setSettingsRequest(request);
    setRequestSettings({
      response_time_hours: request.response_time_hours || 48,
      penalty_rate_per_day: request.penalty_rate_per_day || 0,
    });
  };

  const saveSettings = async () => {
    if (!settingsRequest) return;
    try {
      await api.put(`/client-requests/${settingsRequest.id}`, requestSettings);
      toast.success("Request settings saved");
      setSettingsRequest(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/client-requests/${requestId}`);
      toast.success("Request deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openViewRequest = async (requestId) => {
    setViewLoading(true);
    try {
      const res = await api.get(`/client-requests/${requestId}`);
      setViewRequest(res.data);
    } catch (error) {
      toast.error("Failed to load request details");
    } finally {
      setViewLoading(false);
    }
  };

  const FIELD_ICONS = {
    checkbox: CheckCircle2,
    text: FileText,
    number: Hash,
    dropdown: ListChecks,
    file: Upload,
    date: Calendar,
    textarea: AlignLeft,
    url: LinkIcon,
    email: Mail,
  };

  const formatTime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = Math.floor(hours / 24);
    const h = Math.round(hours % 24);
    return `${days}d ${h}h`;
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case "info_request": return "Info Request";
      case "work_review": return "Work Review";
      case "approval_request": return "Approval";
      case "payment_request": return "Payment";
      case "scope_approval": return "Scope Change";
      default: return type;
    }
  };

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case "info_request": return <FileText className="h-4 w-4 text-sky-400" />;
      case "work_review": return <Eye className="h-4 w-4 text-orange-400" />;
      case "approval_request": return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case "payment_request": return <DollarSign className="h-4 w-4 text-amber-400" />;
      case "scope_approval": return <AlertTriangle className="h-4 w-4 text-purple-400" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  // Filter requests (sorted: pending first)
  const filteredRequests = useMemo(() => {
    const statusOrder = { pending: 0, responded: 1, overdue: 0 };
    return requests.filter(r => {
      if (clientFilter !== "all" && r.client_id !== clientFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (managerFilter !== "all" && r.created_by_id !== managerFilter) return false;
      return true;
    }).sort((a, b) => (statusOrder[a.status] ?? 2) - (statusOrder[b.status] ?? 2));
  }, [requests, clientFilter, statusFilter, managerFilter]);

  // Stats for outgoing
  const outgoingStats = useMemo(() => {
    const pending = requests.filter(r => r.status === "pending");
    const overdue = pending.filter(r => r.is_overdue);
    const responded = requests.filter(r => r.status === "responded");
    const studioPending = studioRequests.filter(r => r.status === "pending").length;
    const studioResponded = studioRequests.filter(r => r.status === "approved" || r.status === "rejected").length;
    return { 
      pending: pending.length + studioPending, 
      overdue: overdue.length, 
      responded: responded.length + studioResponded, 
      total: requests.length + studioRequests.length 
    };
  }, [requests, studioRequests]);

  // Stats for incoming
  const incomingStats = useMemo(() => {
    const pending = priceRequests.filter(r => r.status === "pending");
    const approved = priceRequests.filter(r => r.status === "approved");
    const rejected = priceRequests.filter(r => r.status === "rejected");
    return { pending: pending.length, approved: approved.length, rejected: rejected.length, total: priceRequests.length };
  }, [priceRequests]);

  // Filter incoming price requests by status tab
  const filteredPriceRequests = useMemo(() => {
    return priceRequests.filter(r => r.status === priceStatusFilter)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [priceRequests, priceStatusFilter]);

  // Filter studio requests (outgoing to partners) by status
  const filteredStudioRequests = useMemo(() => {
    if (statusFilter === "all") return studioRequests;
    if (statusFilter === "pending") return studioRequests.filter(r => r.status === "pending");
    if (statusFilter === "responded") return studioRequests.filter(r => r.status === "approved" || r.status === "rejected");
    return studioRequests;
  }, [studioRequests, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="client-requests-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">
            Financial Requests
          </h1>
          <p className="text-sm text-zinc-500">
            Outgoing requests to clients & incoming from partners
          </p>
        </div>
        {activeTab === "outgoing" && <CreateClientRequestDialog onCreated={fetchData} />}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0.5 border-b border-zinc-800 w-fit" data-testid="request-tabs">
        <button
          onClick={() => { setActiveTab("outgoing"); setStatusFilter("pending"); }}
          className={cn("px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2",
            activeTab === "outgoing" ? "text-orange-400 border-orange-400" : "text-zinc-500 border-transparent hover:text-zinc-300")}
          data-testid="tab-outgoing"
        >
          <ArrowUpRight className="h-4 w-4" />
          Outgoing ({outgoingStats.total})
        </button>
        <button
          onClick={() => { setActiveTab("incoming"); setStatusFilter("all"); }}
          className={cn("px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2",
            activeTab === "incoming" ? "text-blue-400 border-blue-400" : "text-zinc-500 border-transparent hover:text-zinc-300")}
          data-testid="tab-incoming"
        >
          <ArrowDownLeft className="h-4 w-4" />
          Incoming ({incomingStats.total})
        </button>
      </div>

      {/* Outgoing Tab */}
      {activeTab === "outgoing" && (
        <>
          {/* Status Pills + Filters Row */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              {[
                { value: "all", label: "All", count: filteredRequests.length + studioRequests.length, color: "zinc" },
                { value: "pending", label: "Pending", count: outgoingStats.pending, color: "yellow" },
                { value: "responded", label: "Responded", count: outgoingStats.responded, color: "green" },
              ].map(({ value, label, count, color }) => (
                <button
                  key={value}
                  onClick={() => setStatusFilter(value)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                    statusFilter === value
                      ? "border"
                      : "bg-zinc-800/50 text-zinc-500 border border-zinc-800 hover:text-zinc-300"
                  )}
                  style={statusFilter === value ? {
                    backgroundColor: color === "yellow" ? "rgba(234,179,8,0.15)" : color === "green" ? "rgba(34,197,94,0.15)" : "rgba(113,113,122,0.15)",
                    color: color === "yellow" ? "#facc15" : color === "green" ? "#4ade80" : "#a1a1aa",
                    borderColor: color === "yellow" ? "rgba(234,179,8,0.3)" : color === "green" ? "rgba(34,197,94,0.3)" : "rgba(113,113,122,0.3)",
                  } : {}}
                  data-testid={`outgoing-status-tab-${value}`}
                >
                  {label}
                  <span className="text-xs opacity-70">{count}</span>
                </button>
              ))}
              {outgoingStats.overdue > 0 && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs px-3 py-1.5">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {outgoingStats.overdue} overdue
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="w-40 h-9 bg-zinc-900 border-zinc-800 text-xs">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={managerFilter} onValueChange={setManagerFilter}>
                <SelectTrigger className="w-40 h-9 bg-zinc-900 border-zinc-800 text-xs">
                  <SelectValue placeholder="All Managers" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Managers</SelectItem>
                  {managers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

      {/* Client Requests Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Request</TableHead>
              <TableHead className="text-zinc-400">Client</TableHead>
              <TableHead className="text-zinc-400">Manager</TableHead>
              <TableHead className="text-zinc-400">Type</TableHead>
              <TableHead className="text-zinc-400 text-right">Amount</TableHead>
              <TableHead className="text-zinc-400">Timer</TableHead>
              <TableHead className="text-zinc-400 text-center">Status</TableHead>
              <TableHead className="text-zinc-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequests.map((req) => (
              <TableRow 
                key={req.id} 
                className="border-zinc-800 cursor-pointer hover:bg-zinc-800/50"
                onClick={() => navigate(`/client-requests/${req.id}`)}
              >
                <TableCell>
                  <div>
                    <p className="text-zinc-50 font-medium hover:text-orange-400 transition-colors">{req.title}</p>
                    <p className="text-xs text-zinc-500">
                      {req.project_name && `${req.project_name}`}
                      {req.task_name && ` • ${req.task_name}`}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-zinc-300">{req.client_name || "—"}</span>
                </TableCell>
                <TableCell>
                  <span className="text-zinc-400">{req.created_by_name || "—"}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {getRequestTypeIcon(req.request_type)}
                    <span className="text-sm text-zinc-400">
                      {getRequestTypeLabel(req.request_type)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {(req.proposed_price || req.base_price) ? (
                    <div>
                      <span className="text-zinc-50 font-mono text-sm">
                        ${((req.proposed_price || req.base_price) * (req.quantity || 1)).toLocaleString()}
                      </span>
                      {req.quantity > 1 && (
                        <p className="text-[10px] text-zinc-500 font-mono">
                          ${req.proposed_price || req.base_price} × {req.quantity}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-zinc-600">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {req.status === "pending" ? (
                    <div className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono",
                      req.is_overdue 
                        ? "bg-red-500/20 text-red-400" 
                        : "bg-zinc-800 text-zinc-400"
                    )}>
                      <Clock className="h-3 w-3" />
                      {req.is_overdue ? "OVERDUE" : formatTime(req.time_elapsed_hours)}
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-500">
                      {formatTime(req.time_elapsed_hours)} response
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    req.status === "pending" 
                      ? req.is_overdue 
                        ? "bg-red-500/10 text-red-400 border-red-500/30"
                        : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                      : "bg-green-500/10 text-green-400 border-green-500/30"
                  )}>
                    {req.status === "responded" ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {req.response_type}
                      </span>
                    ) : (
                      req.is_overdue ? "OVERDUE" : "Pending"
                    )}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/client-requests/${req.id}`)}
                      className="h-8 w-8 p-0 text-zinc-500 hover:text-sky-400"
                      title="View request details"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openSettings(req)}
                      className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-50"
                      title="Edit request settings"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteRequest(req.id)}
                      className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
                      title="Delete request"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredRequests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                  No requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </CardContent>
      </Card>

      {/* Request Settings Dialog */}
      <Dialog open={!!settingsRequest} onOpenChange={() => setSettingsRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Request Settings
            </DialogTitle>
            <p className="text-xs text-zinc-500 mt-1">{settingsRequest?.title}</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400">Response Time (hours)</Label>
              <Input
                type="number"
                value={requestSettings.response_time_hours}
                onChange={(e) => setRequestSettings({
                  ...requestSettings,
                  response_time_hours: parseInt(e.target.value) || 48
                })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
              <p className="text-xs text-zinc-500">
                Time client has to respond before marked overdue
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Penalty Rate (% per day)</Label>
              <Input
                type="number"
                step="0.1"
                value={requestSettings.penalty_rate_per_day}
                onChange={(e) => setRequestSettings({
                  ...requestSettings,
                  penalty_rate_per_day: parseFloat(e.target.value) || 0
                })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
              <p className="text-xs text-zinc-500">
                Overrides project rate. Leave 0 to use project default.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSettingsRequest(null)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={saveSettings} className="bg-orange-500 hover:bg-orange-600">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={!!viewRequest} onOpenChange={() => setViewRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Eye className="h-5 w-5 text-sky-400" />
              Request Details
            </DialogTitle>
          </DialogHeader>
          
          {viewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : viewRequest && (
            <div className="space-y-4 py-2">
              {/* Title and Status */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-zinc-50">{viewRequest.title}</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    viewRequest.status === "pending" 
                      ? viewRequest.is_overdue 
                        ? "bg-red-500/10 text-red-400 border-red-500/30"
                        : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                      : "bg-green-500/10 text-green-400 border-green-500/30"
                  )}>
                    {viewRequest.status === "responded" ? "Responded" : viewRequest.is_overdue ? "OVERDUE" : "Pending"}
                  </Badge>
                  {viewRequest.template_name && (
                    <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                      {viewRequest.template_name}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <div>
                  <p className="text-xs text-zinc-500">Client</p>
                  <p className="text-sm text-zinc-50">{viewRequest.client_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Created By</p>
                  <p className="text-sm text-zinc-50">{viewRequest.created_by_name || "—"}</p>
                </div>
                {viewRequest.project_name && (
                  <div>
                    <p className="text-xs text-zinc-500">Project</p>
                    <p className="text-sm text-zinc-50">{viewRequest.project_name}</p>
                  </div>
                )}
                {viewRequest.task_name && (
                  <div>
                    <p className="text-xs text-zinc-500">Task</p>
                    <p className="text-sm text-zinc-50">{viewRequest.task_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-500">Time Elapsed</p>
                  <p className="text-sm text-zinc-50">{formatTime(viewRequest.time_elapsed_hours)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Type</p>
                  <p className="text-sm text-zinc-50">{getRequestTypeLabel(viewRequest.request_type)}</p>
                </div>
              </div>

              {/* Description */}
              {viewRequest.description && (
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Description</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{viewRequest.description}</p>
                </div>
              )}

              {/* Template Fields with Submitted Data */}
              {viewRequest.template_fields && viewRequest.template_fields.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">
                    {viewRequest.status === "responded" ? "Client Submitted Data" : "Fields Client Needs to Fill"}
                  </p>
                  <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                    {viewRequest.template_fields.map((field) => {
                      const Icon = FIELD_ICONS[field.field_type] || FileText;
                      const value = viewRequest.template_data?.[field.id];
                      const hasValue = value && (typeof value === "string" ? value.trim() !== "" : true);
                      
                      return (
                        <div key={field.id} className="py-2 px-2 bg-zinc-800/50 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                            <span className="text-sm text-zinc-400 font-medium">{field.label}</span>
                            {field.is_required && !hasValue && viewRequest.status === "pending" && (
                              <span className="text-red-400 text-xs">*</span>
                            )}
                            <Badge variant="outline" className="ml-auto text-[10px] h-5 bg-zinc-800 text-zinc-500 border-zinc-700">
                              {field.field_type}
                            </Badge>
                          </div>
                          {/* Show submitted value */}
                          {hasValue ? (
                            <div className="mt-1 ml-5">
                              {field.field_type === "file" ? (
                                <a 
                                  href={value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1"
                                >
                                  <Upload className="h-3 w-3" />
                                  Download File
                                </a>
                              ) : field.field_type === "url" ? (
                                <a 
                                  href={value.startsWith("http") ? value : `https://${value}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1 break-all"
                                >
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  {value}
                                </a>
                              ) : field.field_type === "checkbox" ? (
                                <span className={cn("text-sm", value ? "text-green-400" : "text-zinc-500")}>
                                  {value ? "✓ Yes" : "✗ No"}
                                </span>
                              ) : (
                                <p className="text-zinc-50 text-sm whitespace-pre-wrap">{value}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-zinc-600 text-sm italic mt-1 ml-5">
                              {viewRequest.status === "pending" ? "Not yet filled" : "Not provided"}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Client Response (if responded) */}
              {viewRequest.status === "responded" && (
                <div className="space-y-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-green-400 uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Client Response: {viewRequest.response_type}
                  </p>
                  {viewRequest.response_comment && (
                    <p className="text-sm text-zinc-300">{viewRequest.response_comment}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewRequest(null)} className="border-zinc-700">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

        {/* Studio Requests (outgoing to partners) */}
        {filteredStudioRequests.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4 text-blue-400" />
              Price Agreements to Partners ({filteredStudioRequests.length})
            </h3>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Title</TableHead>
                    <TableHead className="text-zinc-400">Project</TableHead>
                    <TableHead className="text-zinc-400">Type</TableHead>
                    <TableHead className="text-zinc-400 text-right">Price</TableHead>
                    <TableHead className="text-zinc-400 text-center">Qty</TableHead>
                    <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                    <TableHead className="text-zinc-400 text-center">Status</TableHead>
                    <TableHead className="text-zinc-400">Date</TableHead>
                    <TableHead className="text-zinc-400 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudioRequests.map((req) => (
                    <TableRow key={req.id} className="border-zinc-800 hover:bg-zinc-800/30">
                      <TableCell className="py-1.5">
                        <p className="text-sm text-zinc-50">{req.title}</p>
                        {req.description && <p className="text-xs text-zinc-500 line-clamp-1">{req.description}</p>}
                      </TableCell>
                      <TableCell className="py-1.5 text-xs text-zinc-400">{req.project_name || "—"}</TableCell>
                      <TableCell className="py-1.5 text-xs text-zinc-400">{(req.request_type || "").replace(/_/g, " ")}</TableCell>
                      {editingReq === req.id ? (
                        <>
                          <TableCell className="py-1.5">
                            <input type="number" value={editForm.price_per_unit} onChange={e => setEditForm(f => ({...f, price_per_unit: e.target.value}))} placeholder="Price" className="w-20 px-1 py-0.5 bg-zinc-800 border border-zinc-600 rounded text-xs text-zinc-50 font-mono" />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <input type="number" value={editForm.quantity} onChange={e => setEditForm(f => ({...f, quantity: e.target.value}))} placeholder="Qty" className="w-16 px-1 py-0.5 bg-zinc-800 border border-zinc-600 rounded text-xs text-zinc-50 text-center" />
                          </TableCell>
                          <TableCell className="py-1.5 text-right font-mono text-sm text-emerald-400">
                            {editForm.price_per_unit && editForm.quantity ? `$${(parseFloat(editForm.price_per_unit) * parseInt(editForm.quantity) * (1 - (parseFloat(editForm.discount) || 0) / 100)).toFixed(2)}` : "—"}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="py-1.5 text-right font-mono text-xs text-zinc-400 cursor-pointer hover:text-orange-400" onClick={() => { setEditingReq(req.id); setEditForm({ price_per_unit: req.price_per_unit || "", quantity: req.quantity || "", discount: req.discount || "", amount: req.amount || "" }); }}>
                            {req.price_per_unit ? `$${req.price_per_unit}` : <span className="text-zinc-600 italic">set</span>}
                            {req.discount > 0 && <span className="text-orange-400 ml-1">-{req.discount}%</span>}
                          </TableCell>
                          <TableCell className="py-1.5 text-center text-xs text-zinc-400 cursor-pointer hover:text-orange-400" onClick={() => { setEditingReq(req.id); setEditForm({ price_per_unit: req.price_per_unit || "", quantity: req.quantity || "", discount: req.discount || "", amount: req.amount || "" }); }}>
                            {req.quantity || <span className="text-zinc-600 italic">set</span>}
                          </TableCell>
                          <TableCell className="py-1.5 text-right font-mono text-sm text-zinc-50">
                            {req.amount ? `$${req.amount.toFixed(2)}` : (req.price_per_unit && req.quantity ? `$${(req.price_per_unit * req.quantity * (1 - (req.discount || 0) / 100)).toFixed(2)}` : <span className="text-zinc-600">—</span>)}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="py-1.5 text-center">
                        <Badge className={cn("text-[10px]",
                          req.status === "pending" && "bg-blue-500/20 text-blue-400",
                          req.status === "approved" && "bg-emerald-500/20 text-emerald-400",
                          req.status === "rejected" && "bg-red-500/20 text-red-400"
                        )}>{req.status}</Badge>
                      </TableCell>
                      <TableCell className="py-1.5 text-xs text-zinc-500">{req.created_at ? new Date(req.created_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="py-1.5">
                        <div className="flex items-center gap-1">
                          {editingReq === req.id ? (
                            <>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-400 hover:text-emerald-300"
                                onClick={async () => {
                                  try {
                                    await api.patch(`/studio-requests/${req.id}`, {
                                      price_per_unit: editForm.price_per_unit ? parseFloat(editForm.price_per_unit) : null,
                                      quantity: editForm.quantity ? parseInt(editForm.quantity) : null,
                                      discount: editForm.discount ? parseFloat(editForm.discount) : null,
                                    });
                                    toast.success("Price updated");
                                    setEditingReq(null);
                                    fetchData();
                                  } catch (e) { toast.error("Failed to save"); }
                                }}
                                data-testid={`save-price-${req.id}`}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-zinc-500 hover:text-zinc-300" onClick={() => setEditingReq(null)}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-zinc-500 hover:text-red-400"
                              onClick={async () => {
                                if (!window.confirm("Delete this request?")) return;
                                try {
                                  await api.delete(`/studio-requests/${req.id}`);
                                  toast.success("Request deleted");
                                  fetchData();
                                } catch (e) { toast.error("Failed to delete"); }
                              }}
                              data-testid={`delete-studio-req-${req.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </CardContent>
            </Card>
          </div>
        )}
        </>
      )}

      {/* Incoming Tab — Price Requests from partners */}
      {activeTab === "incoming" && (
        <>
          {/* Status Sub-tabs */}
          <div className="flex items-center gap-2">
            {[
              { value: "pending", label: "Pending", count: incomingStats.pending, color: "yellow" },
              { value: "approved", label: "Approved", count: incomingStats.approved, color: "green" },
              { value: "rejected", label: "Rejected", count: incomingStats.rejected, color: "red" },
            ].map(({ value, label, count, color }) => (
              <button
                key={value}
                onClick={() => setPriceStatusFilter(value)}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                  priceStatusFilter === value
                    ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/30`
                    : "bg-zinc-800/50 text-zinc-500 border border-zinc-800 hover:text-zinc-300"
                )}
                style={priceStatusFilter === value ? {
                  backgroundColor: color === "yellow" ? "rgba(234,179,8,0.15)" : color === "green" ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                  color: color === "yellow" ? "#facc15" : color === "green" ? "#4ade80" : "#f87171",
                  borderColor: color === "yellow" ? "rgba(234,179,8,0.3)" : color === "green" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)",
                } : {}}
                data-testid={`price-status-tab-${value}`}
              >
                {label}
                <span className="text-xs opacity-70">{count}</span>
              </button>
            ))}
          </div>

          {/* Incoming Table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Task</TableHead>
                    <TableHead className="text-zinc-400">Project</TableHead>
                    <TableHead className="text-zinc-400">Partner</TableHead>
                    <TableHead className="text-zinc-400">Type</TableHead>
                    <TableHead className="text-zinc-400 text-right">Base Price</TableHead>
                    <TableHead className="text-zinc-400 text-right">Proposed</TableHead>
                    <TableHead className="text-zinc-400 text-center">Discount</TableHead>
                    <TableHead className="text-zinc-400 text-center">Qty</TableHead>
                    <TableHead className="text-zinc-400 text-right">Total</TableHead>
                    <TableHead className="text-zinc-400 text-center">Status</TableHead>
                    <TableHead className="text-zinc-400">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPriceRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-zinc-500 py-8">
                        No incoming price requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPriceRequests.map((req) => {
                      const basePrice = req.base_price || 0;
                      const proposed = req.proposed_price || 0;
                      const qty = req.quantity || 1;
                      const discount = basePrice > 0 && basePrice > proposed ? Math.round((1 - proposed / basePrice) * 100) : 0;
                      const total = proposed * qty;
                      return (
                        <TableRow key={req.id} className="border-zinc-800 hover:bg-zinc-800/30 cursor-pointer"
                          onClick={() => req.project_id && navigate(`/sales/project/${req.project_id}`)}
                          data-testid={`incoming-req-${req.id}`}
                        >
                          <TableCell className="text-zinc-50 text-sm font-medium">{req.task_name || "—"}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">{req.project_name || "—"}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">{req.partner_name || "—"}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">{req.task_type?.replace("_", " ") || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-zinc-500 text-sm">{basePrice > 0 ? `$${basePrice.toLocaleString()}` : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-zinc-50 text-sm">${proposed.toLocaleString()}</TableCell>
                          <TableCell className="text-center">
                            {discount > 0 ? (
                              <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">-{discount}%</Badge>
                            ) : (
                              <span className="text-zinc-600">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center text-zinc-400 text-sm">{qty}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-400 text-sm font-medium">${total.toLocaleString()}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={cn("text-xs",
                              req.status === "pending" && "bg-yellow-500/20 text-yellow-400",
                              req.status === "approved" && "bg-emerald-500/20 text-emerald-400",
                              req.status === "rejected" && "bg-red-500/20 text-red-400"
                            )}>
                              {req.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-500 text-xs">{req.created_at?.slice(0, 10)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ClientRequestsPage;
